/**
 * Phase 66 Plan 04 — `/api/v1/circuits` endpoint wrappers (Phase 57).
 *
 * Used by:
 *   - Provisioning approve modal (D-15 combobox search)
 *   - Machines page circuit filter dropdown (super_admin / admin)
 *   - Users page per-circuit drilldown
 *
 * @see PATTERNS.md §API endpoints catalog
 */

import { apiDel, apiGet, apiPatch, apiPost } from "./client";

export interface Circuit {
  id: string;
  name: string;
  /** Phase 57 slug — immutable on edit. */
  slug?: string;
  /** Optional customer contact info. */
  customer_contact?: string | null;
  /** Phase 72 D-DT-08 — number of digital twin versions retained per
   *  machine in this circuit. Defaults to 1 (current-only). */
  digital_twin_retention_versions?: number;
  /** ISO timestamp. */
  created_at?: string;
}

export interface CircuitStats {
  circuit_id?: string;
  machines_total?: number;
  machines_online?: number;
  users_total?: number;
  /** Legacy backend keys (Phase 57 stats). */
  machine_count?: number;
  user_count?: number;
  balance_total?: number;
  coupon_count?: number;
}

/** Combined circuit + stats row (used by AdminCircuitsPage). */
export type CircuitWithStats = Circuit & CircuitStats;

/** GET /api/v1/admin/circuits. */
export const listCircuits = (): Promise<Circuit[]> =>
  apiGet<Circuit[]>("/admin/circuits");

/** GET /api/v1/admin/circuits/{id}. */
export const getCircuit = (id: string): Promise<Circuit> =>
  apiGet<Circuit>(`/admin/circuits/${encodeURIComponent(id)}`);

/** GET /api/v1/admin/circuits/{id}/stats. */
export const getCircuitStats = (id: string): Promise<CircuitStats> =>
  apiGet<CircuitStats>(`/admin/circuits/${encodeURIComponent(id)}/stats`);

/** POST /api/v1/admin/circuits. Phase 57 backend accepts {name, slug, customer_contact};
 *  Phase 72 adds digital_twin_retention_versions (D-DT-08, default 1). */
export const createCircuit = (body: {
  name: string;
  slug: string;
  customer_contact?: string | null;
  digital_twin_retention_versions?: number;
}): Promise<Circuit> => apiPost<Circuit>("/admin/circuits", body);

/** PATCH /api/v1/admin/circuits/{id}. */
export const patchCircuit = (
  id: string,
  body: Partial<Omit<Circuit, "id">>,
): Promise<Circuit> =>
  apiPatch<Circuit>(`/admin/circuits/${encodeURIComponent(id)}`, body);

/** DELETE /api/v1/admin/circuits/{id}. */
export const deleteCircuit = (id: string): Promise<unknown> =>
  apiDel(`/admin/circuits/${encodeURIComponent(id)}`);
